import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, EMAIL_CATEGORY_COPY, type EmailPreferences, type OptionalEmailCategory } from "@atlas/shared";
import { AtlasPanel } from "./AtlasPanel";
import { Skel } from "./Skeleton";
import { EMAIL_PREFERENCES_KEY, fetchEmailPreferences, updateEmailPreferences } from "../api/email";

// ─── Email preferences card (spec 16) ────────────────────────────────────────
// Three opt-out categories; account emails (verification, password resets,
// billing, data exports, security) have no switch and the copy says so.
// Mirrored on mobile: app/(tabs)/profile/email.tsx.

const CATEGORIES: OptionalEmailCategory[] = ["activity", "digest", "product"];

export function EmailPreferencesPanel() {
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: EMAIL_PREFERENCES_KEY,
    queryFn: fetchEmailPreferences,
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<EmailPreferences>) => updateEmailPreferences(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: EMAIL_PREFERENCES_KEY });
      const previous = queryClient.getQueryData<{ preferences: EmailPreferences }>(EMAIL_PREFERENCES_KEY);
      if (previous) {
        queryClient.setQueryData(EMAIL_PREFERENCES_KEY, {
          preferences: { ...previous.preferences, ...patch },
        });
      }
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(EMAIL_PREFERENCES_KEY, ctx.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(EMAIL_PREFERENCES_KEY, data);
    },
  });

  const prefs = prefsQuery.data?.preferences;

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  };
  const toggleLabelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    flexShrink: 0,
  };

  return (
    <AtlasPanel title="Email">
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {prefsQuery.isLoading && <Skel height={96} />}
        {prefsQuery.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
            Couldn&#39;t load your email settings. Try again in a moment.
          </p>
        )}
        {prefs &&
          CATEGORIES.map((category, i) => (
            <React.Fragment key={category}>
              {i > 0 && <div style={{ height: 1, background: "var(--border-subtle)" }} />}
              <div style={rowStyle}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                    {EMAIL_CATEGORY_COPY[category].title}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, maxWidth: 420, lineHeight: 1.5 }}>
                    {EMAIL_CATEGORY_COPY[category].hint}
                  </div>
                </div>
                <label style={toggleLabelStyle}>
                  <input
                    type="checkbox"
                    checked={prefs[category]}
                    disabled={mutation.isPending}
                    onChange={(e) => mutation.mutate({ [category]: e.target.checked })}
                    style={{ width: 16, height: 16, accentColor: "var(--accent)" }}
                  />
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{prefs[category] ? "On" : "Off"}</span>
                </label>
              </div>
            </React.Fragment>
          ))}
        {mutation.isError && (
          <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
            {errorMessage(mutation.error, "Couldn't save that. Try again.")}
          </p>
        )}
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
          Account emails always send: verification, password resets, billing and Pro status changes,
          data exports and security notices. Every other email has an unsubscribe link in its footer.
        </p>
      </div>
    </AtlasPanel>
  );
}
